import { PriceDisplay, RangeHorizon, ResampleIntervals } from '@chart';
import { ButtonLink } from '@design/components/button-link/ButtonLink';
import { Icon } from '@design/components/icon/Icon';
import { SkeletonLoader } from '@design/components/skeleton-loader/SkeletonLoader';
import { ChartAreaWrapper } from '@terminal/components/chart-area-wrapper/ChartAreaWrapper';
import {
  ChartNavigateConfigParams,
  useChartNavigate,
} from '@terminal/hooks/use-chart-navigate/useChartNavigate';
import { ChartArea } from '@terminal/modules/simple-chart/components/chart-area/ChartArea';
import { PosthogTrackingSource } from '@terminal/services/tracking/posthog/constants';
import { ChartSearchParamsAssetValue } from '@terminal/utils/chart-search-utils/chart-search-utils';
import { UseChartThemeReturn } from '@terminal/views/turbo-chart/hooks/use-chart-theme/useChartTheme';
import { useTurboChart } from '@terminal/views/turbo-chart/hooks/use-turbo-chart/useTurboChart';
import { ValidChartSettings } from '@terminal/views/turbo-chart/utils/chart-settings-utils/chart-settings-utils';
import clsx from 'clsx';
import React, { RefObject, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { ChartPerSnakeRef } from '../ChartPeerGroup';
import styles from './ChartPerSnake.module.scss';

export interface ChartPerSnakeProps {
  chartAssets: ValidChartSettings['assets'];
  horizon: RangeHorizon;
  colors: UseChartThemeReturn['colors'];
  chartRef: RefObject<ChartPerSnakeRef>;
  isSingle: boolean;
  initialPriceDisplay: PriceDisplay;
  withHeader: boolean;
  enablePriceHover?: boolean;
  hideYAxisLines?: boolean;
  displayAxisUnit?: boolean;
  source: PosthogTrackingSource;
}

export const ChartPerSnake = ({
  chartAssets,
  horizon,
  colors,
  chartRef,
  isSingle,
  initialPriceDisplay,
  withHeader,
  enablePriceHover = true,
  hideYAxisLines = false,
  displayAxisUnit = true,
  source,
}: ChartPerSnakeProps) => {
  const { t } = useTranslation(['chart']);
  const canvasRef: RefObject<HTMLCanvasElement | null> = useRef(null);
  const paneSettings: ChartNavigateConfigParams = {
    yAxisType: initialPriceDisplay === 'price' ? 'split' : 'merged',
  };

  const snakes = chartAssets.map(item => item.snake);
  const initialChartSettings = useMemo(
    () => ({
      assets: chartAssets,
      resample: ResampleIntervals.OneDay,
      horizon: horizon,
      domain: null,
      hidden: undefined,
      paneSettings: {
        0: paneSettings,
      },
    }),
    [snakes.toString()]
  );

  const {
    assetWithError,
    chartData,
    chartState,
    hasEmpty,
    hasError,
    primaryAsset,
    primaryPane,
    retryInitialChartLoad,
    changeRange,
    hideActiveAsset,
    chartIsLoading,
  } = useTurboChart({
    initialChartSettings,
    shouldUseSearchParams: false,
  });

  const chartNavigate = useChartNavigate();

  const hasInsufficientData = chartData[0]?.ts.length === 1;

  const hasEmptyState = hasEmpty || hasInsufficientData;

  const onClick = () => {
    const assets: ChartSearchParamsAssetValue[] = chartData.map(asset => [
      asset.entity.tag,
      asset.snakeMeta?.snake ?? asset.entity.default_snake,
      0,
    ]);

    chartNavigate(assets, {
      horizon: chartState.horizon,
      resample: chartState.resample,
      ...paneSettings,
    });
  };

  chartRef.current = {
    changeRange,
    hideActiveAsset,
  };

  return (
    <div
      className={clsx(styles.container, {
        [styles.isSingle]: isSingle,
      })}
    >
      {chartIsLoading ? (
        <div
          className={styles.skeletonChartLoaderWrapper}
          data-testid="chart-per-snake-loading"
        >
          <SkeletonLoader
            areas={[{ styles: { width: '100%', borderRadius: '8px' } }]}
          />
        </div>
      ) : (
        <>
          {withHeader && (
            <div className={styles.header}>
              <p className={styles.snake}>
                {primaryAsset?.snakeMeta?.name.english}
              </p>
              <ButtonLink
                data-testid="open-in-chart"
                label={
                  <>
                    {t('chart:openIn')}
                    <Icon iconName={'ChartMixed'} size={16} />
                    {t('chart:title')}
                  </>
                }
                onClick={onClick}
              />
            </div>
          )}
          <ChartAreaWrapper
            className={styles.styledChartAreaWrapper}
            assetWithError={assetWithError}
            hasError={hasError}
            hasEmpty={hasEmptyState}
            colors={colors}
            chartData={chartData}
            onRetry={retryInitialChartLoad}
            chartConfig={{ enableZoom: false, hideYAxisLines }}
            renderChartArea={options => {
              if (!primaryAsset || !primaryPane) {
                return null;
              }

              return (
                <ChartArea
                  canvasRef={canvasRef}
                  options={options}
                  chartState={chartState}
                  primaryAsset={primaryAsset}
                  primaryPane={primaryPane}
                  tooltipSortConfig={{ field: 'priceFormatted', desc: true }}
                  enablePriceHover={enablePriceHover}
                  axisUnit={
                    initialPriceDisplay === 'percentage'
                      ? '%'
                      : chartData[0]?.entity.currency
                  }
                  displayAxisUnit={displayAxisUnit}
                  source={source}
                />
              );
            }}
          />
        </>
      )}
    </div>
  );
};
